import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getTenants, createTenant, updateTenant, deleteTenant, getTenantUsers, createTenantUser, updateTenantUser, deleteTenantUser, getTenantEdges, createTenantEdge, updateEdge, deleteEdge, getTenantLocations, createTenantLocation, updateTenantLocation, deleteTenantLocation, removeEdgeFromLocation } from "../api/tenantApi";
import { toast } from "react-toastify";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CloseIcon from "@mui/icons-material/Close";
import RouterIcon from "@mui/icons-material/Router";
import DashboardIcon from "@mui/icons-material/Dashboard";

function TenantRow({ tenant, accessToken, me, onRefresh }) {
  const navigate = useNavigate();

  // Ana tenant açılır/kapanır
  const [tenantOpen, setTenantOpen] = useState(false);

  // Tenant düzenleme state'leri
  const [showEditTenantModal, setShowEditTenantModal] = useState(false);
  const [submittingTenant, setSubmittingTenant] = useState(false);
  const [editTenantForm, setEditTenantForm] = useState({
    name: tenant.name || "",
    contact_person: tenant.contact_person || "",
  });

  // Tenant silme state'leri
  const [showDeleteTenantDialog, setShowDeleteTenantDialog] = useState(false);
  const [deletingTenant, setDeletingTenant] = useState(false);

  // Alt bölümler için ayrı açılır/kapanır state'ler - lazy loading için
  const [usersOpen, setUsersOpen] = useState(false);
  const [edgesOpen, setEdgesOpen] = useState(false);
  const [usersFetched, setUsersFetched] = useState(false);
  const [edgesFetched, setEdgesFetched] = useState(false);

  // User states
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [submittingUser, setSubmittingUser] = useState(false);
  const [userForm, setUserForm] = useState({
    email: "",
    full_name: "",
    password: "",
    tenant_role: "operatör",
  });

  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({
    full_name: "",
    email: "",
    tenant_role: "",
    password: "",
  });

  const [showDeleteUserDialog, setShowDeleteUserDialog] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [deletingUserLoading, setDeletingUserLoading] = useState(false);

  // Edge states (server-side pagination)
  const [edges, setEdges] = useState([]);
  const [edgesTotal, setEdgesTotal] = useState(0);
  const [edgesOffset, setEdgesOffset] = useState(0);
  const [edgesLimit] = useState(20); // Sayfa başına kayıt
  const [loadingEdges, setLoadingEdges] = useState(false);
  const [loadingMoreEdges, setLoadingMoreEdges] = useState(false);
  // eslint-disable-next-line no-unused-vars -- Edge modalı yorumda, ileride açılacak
  const [showEdgeModal, setShowEdgeModal] = useState(false); // Dialog commented out; keep for future use
  const [submittingEdge, setSubmittingEdge] = useState(false);
  const [edgeForm, setEdgeForm] = useState({
    edgeId: "",
    location_id: "",
    device: { manufacturer: "", model: "", firmware: "" },
    asset: { assetId: "", type: "", location: "" },
  });

  const [showEditEdgeModal, setShowEditEdgeModal] = useState(false);
  const [editingEdge, setEditingEdge] = useState(null);
  const [editEdgeForm, setEditEdgeForm] = useState({
    edgeId: "",
    location_id: "",
    device: { manufacturer: "", model: "", firmware: "" },
    asset: { assetId: "", type: "", location: "" },
  });

  const [showDeleteEdgeDialog, setShowDeleteEdgeDialog] = useState(false);
  const [deletingEdge, setDeletingEdge] = useState(null);
  const [deletingEdgeLoading, setDeletingEdgeLoading] = useState(false);

  const canManageUsers = me?.global_role === "superadmin" || me?.tenant_role === "admin";

  // Location states 
  const [locations, setLocations] = useState([]);
  const [locationsOpen, setLocationsOpen] = useState(false);
  const [locationsFetched, setLocationsFetched] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [submittingLocation, setSubmittingLocation] = useState(false);
  const [locationForm, setLocationForm] = useState({
    name: "",
    address: "",
  });

  const [showEditLocationModal, setShowEditLocationModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [editLocationForm, setEditLocationForm] = useState({
    name: "",
    address: "",
  });

  const [showDeleteLocationDialog, setShowDeleteLocationDialog] = useState(false);
  const [deletingLocation, setDeletingLocation] = useState(null);
  const [deletingLocationLoading, setDeletingLocationLoading] = useState(false);


  // Lokasyon child row states
  const [openLocationIds, setOpenLocationIds] = useState(new Set()); // Hangi lokasyonlar açık
  const [locationEdges, setLocationEdges] = useState({}); // {locationId: [edges]}
  const [loadingLocationEdges, setLoadingLocationEdges] = useState({}); // {locationId: boolean}

  // Edge'i lokasyondan çıkarma dialog state'leri
  const [showRemoveEdgeDialog, setShowRemoveEdgeDialog] = useState(false);
  const [removingEdge, setRemovingEdge] = useState(null);
  const [removingEdgeLoading, setRemovingEdgeLoading] = useState(false);


  // Tenant düzenleme
  const handleUpdateTenant = async () => {
    if (!editTenantForm.name.trim()) {
      toast.error("Tenant adı boş olamaz!");
      return;
    }
    setSubmittingTenant(true);
    try {
      await updateTenant(accessToken, tenant.id, editTenantForm);
      toast.success("Tenant başarıyla güncellendi!");
      setShowEditTenantModal(false);
      onRefresh();
    } catch (err) {
      console.error("Tenant güncellenemedi:", err);
      toast.error(err?.response?.data?.detail || "Tenant güncellenemedi!");
    } finally {
      setSubmittingTenant(false);
    }
  };

  // Tenant silme
  const handleDeleteTenant = async () => {
    setDeletingTenant(true);
    try {
      const result = await deleteTenant(accessToken, tenant.id);
      const deletedEdges = result.deleted_edges || 0;
      const deletedUsers = result.deleted_users || 0;

      if (deletedEdges > 0 || deletedUsers > 0) {
        toast.success(`"${tenant.name}" silindi! (${deletedUsers} kullanıcı, ${deletedEdges} edge cihaz)`);
      } else {
        toast.success(`"${tenant.name}" başarıyla silindi!`);
      }
      setShowDeleteTenantDialog(false);
      onRefresh();
    } catch (err) {
      console.error("Tenant silinemedi:", err);
      toast.error(err?.response?.data?.detail || "Tenant silinemedi!");
    } finally {
      setDeletingTenant(false);
    }
  };

  // Kullanıcıları getir
  const fetchUsers = async () => {
    if (!accessToken) return;
    setLoadingUsers(true);
    try {
      const data = await getTenantUsers(accessToken, tenant.id);
      setUsers(data.items || data || []);
      setUsersFetched(true);
    } catch (err) {
      console.error("Kullanıcılar alınamadı:", err);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Edge cihazları getir (server-side pagination)
  const fetchEdges = async (loadMore = false) => {
    if (!accessToken) return;

    if (loadMore) {
      setLoadingMoreEdges(true);
    } else {
      setLoadingEdges(true);
      setEdgesOffset(0);
    }

    try {
      const currentOffset = loadMore ? edgesOffset : 0;
      const data = await getTenantEdges(accessToken, tenant.id, {
        limit: edgesLimit,
        offset: currentOffset,
      });

      const items = data.items || [];
      const total = data.total || 0;

      if (loadMore) {
        setEdges(prev => [...prev, ...items]);
      } else {
        setEdges(items);
      }

      setEdgesTotal(total);
      setEdgesOffset(currentOffset + items.length);
      setEdgesFetched(true);
    } catch (err) {
      console.error("Edge cihazlar alınamadı:", err);
    } finally {
      setLoadingEdges(false);
      setLoadingMoreEdges(false);
    }
  };

  // Kullanıcılar satırına tıklandığında - sadece users fetch edilir
  const handleUsersToggle = () => {
    if (!usersOpen && !usersFetched) {
      fetchUsers();
    }
    setUsersOpen(!usersOpen);
  };

  // Edge satırına tıklandığında - sadece edges fetch edilir
  const handleEdgesToggle = () => {
    if (!edgesOpen && !edgesFetched) {
      fetchEdges();
    }
    setEdgesOpen(!edgesOpen);
  };

  // User form reset
  const resetUserForm = () => {
    setUserForm({
      email: "",
      full_name: "",
      password: "",
      tenant_role: "operatör",
    });
  };

  // Edge form reset
  const resetEdgeForm = () => {
    setEdgeForm({
      edgeId: "",
      location_id: "",
      device: { manufacturer: "", model: "", firmware: "" },
      asset: { assetId: "", type: "", location: "" },
    });
  };

  // Kullanıcı ekleme
  const handleCreateUser = async () => {
    if (!userForm.email.trim()) {
      toast.error("Email boş olamaz!");
      return;
    }
    if (!userForm.full_name.trim()) {
      toast.error("Ad soyad boş olamaz!");
      return;
    }
    if (!userForm.password.trim()) {
      toast.error("Şifre boş olamaz!");
      return;
    }

    setSubmittingUser(true);
    try {
      await createTenantUser(accessToken, tenant.id, userForm);
      toast.success("Kullanıcı başarıyla eklendi!");
      setShowUserModal(false);
      resetUserForm();
      fetchUsers();
    } catch (err) {
      console.error("Kullanıcı eklenemedi:", err);
      toast.error(err?.response?.data?.detail || "Kullanıcı eklenemedi!");
    } finally {
      setSubmittingUser(false);
    }
  };

  // Kullanıcı düzenleme aç
  const handleOpenEditUser = (user) => {
    setEditingUser(user);
    setEditUserForm({
      full_name: user.full_name || "",
      email: user.email || "",
      tenant_role: user.tenant_role || "operatör",
      password: "",
    });
    setShowEditUserModal(true);
  };

  // Kullanıcı güncelleme
  const handleUpdateUser = async () => {
    if (!editUserForm.email.trim()) {
      toast.error("Email boş olamaz!");
      return;
    }
    if (!editUserForm.full_name.trim()) {
      toast.error("Ad soyad boş olamaz!");
      return;
    }

    setSubmittingUser(true);
    try {
      const payload = {
        full_name: editUserForm.full_name,
        email: editUserForm.email,
        tenant_role: editUserForm.tenant_role,
      };

      if (editUserForm.password.trim()) {
        payload.password = editUserForm.password;
      }

      await updateTenantUser(accessToken, tenant.id, editingUser.id, payload);
      toast.success("Kullanıcı başarıyla güncellendi!");
      setShowEditUserModal(false);
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      console.error("Kullanıcı güncellenemedi:", err);
      toast.error(err?.response?.data?.detail || "Kullanıcı güncellenemedi!");
    } finally {
      setSubmittingUser(false);
    }
  };

  // Kullanıcı silme dialog aç
  const handleOpenDeleteUser = (user) => {
    setDeletingUser(user);
    setShowDeleteUserDialog(true);
  };

  // Kullanıcı silme
  const handleConfirmDeleteUser = async () => {
    if (!deletingUser) return;

    setDeletingUserLoading(true);
    try {
      await deleteTenantUser(accessToken, tenant.id, deletingUser.id);
      toast.success("Kullanıcı başarıyla silindi!");
      setShowDeleteUserDialog(false);
      setDeletingUser(null);
      fetchUsers();
    } catch (err) {
      console.error("Kullanıcı silinemedi:", err);
      toast.error(err?.response?.data?.detail || "Kullanıcı silinemedi!");
    } finally {
      setDeletingUserLoading(false);
    }
  };

  // Edge ekleme (Dialog commented out; keep for future use)
  // eslint-disable-next-line no-unused-vars
  const handleCreateEdge = async () => {
    if (!edgeForm.edgeId.trim()) {
      toast.error("Edge ID boş olamaz!");
      return;
    }
    if (!edgeForm.asset.assetId.trim()) {
      toast.error("Cihaz Adı boş olamaz!");
      return;
    }

    setSubmittingEdge(true);
    try {
      await createTenantEdge(accessToken, tenant.id, edgeForm);
      toast.success("Edge cihaz başarıyla eklendi!");
      setShowEdgeModal(false);
      resetEdgeForm();
      fetchEdges();
    } catch (err) {
      console.error("Edge cihaz eklenemedi:", err);
      toast.error(err?.response?.data?.detail || "Edge cihaz eklenemedi!");
    } finally {
      setSubmittingEdge(false);
    }
  };

  // Edge düzenleme aç
  const handleOpenEditEdge = (edge) => {
    if (!locationsFetched) fetchLocations();
    setEditingEdge(edge);
    setEditEdgeForm({
      edgeId: edge.edgeId || "",
      location_id: edge.location_id || "",
      device: {
        manufacturer: edge.device?.manufacturer || "",
        model: edge.device?.model || "",
        firmware: edge.device?.firmware || "",
      },
      asset: {
        assetId: edge.asset?.assetId || "",
        type: edge.asset?.type || "",
        location: edge.asset?.location || "",
      },
    });
    setShowEditEdgeModal(true);
  };

  // Edge güncelleme
  const handleUpdateEdge = async () => {
    if (!editEdgeForm.edgeId.trim()) {
      toast.error("Edge ID boş olamaz!");
      return;
    }

    setSubmittingEdge(true);
    try {
      await updateEdge(accessToken, editingEdge.id, editEdgeForm);
      toast.success("Edge cihaz başarıyla güncellendi!");
      setShowEditEdgeModal(false);
      setEditingEdge(null);
      fetchEdges();
    } catch (err) {
      console.error("Edge cihaz güncellenemedi:", err);
      toast.error(err?.response?.data?.detail || "Edge cihaz güncellenemedi!");
    } finally {
      setSubmittingEdge(false);
    }
  };

  // Edge silme dialog aç
  const handleOpenDeleteEdge = (edge) => {
    setDeletingEdge(edge);
    setShowDeleteEdgeDialog(true);
  };

  // Edge silme
  const handleConfirmDeleteEdge = async () => {
    if (!deletingEdge) return;

    setDeletingEdgeLoading(true);
    try {
      await deleteEdge(accessToken, deletingEdge.id);
      toast.success(`"${deletingEdge.edgeId}" edge cihaz silindi!`);
      setShowDeleteEdgeDialog(false);
      setDeletingEdge(null);
      fetchEdges();
    } catch (err) {
      console.error("Edge cihaz silinemedi:", err);
      toast.error(err?.response?.data?.detail || "Edge cihaz silinemedi!");
    } finally {
      setDeletingEdgeLoading(false);
    }
  };


  // Lokasyonları getir
  const fetchLocations = async () => {
    if (!accessToken) return;
    setLoadingLocations(true);
    try {
      const data = await getTenantLocations(accessToken, tenant.id);
      setLocations(data.items || data || []);
      setLocationsFetched(true);
    } catch (err) {
      console.error("Lokasyonlar alınamadı:", err);
    } finally {
      setLoadingLocations(false);
    }
  };

  // Lokasyon toggle
  const handleLocationsToggle = () => {
    if (!locationsOpen && !locationsFetched) {
      fetchLocations();
    }
    setLocationsOpen(!locationsOpen);
  };

  // Location form reset
  const resetLocationForm = () => {
    setLocationForm({
      name: "",
      address: "",
    });
  };

  // Lokasyon ekleme
  const handleCreateLocation = async () => {
    if (!locationForm.name.trim()) {
      toast.error("Lokasyon adı boş olamaz!");
      return;
    }

    setSubmittingLocation(true);
    try {
      await createTenantLocation(accessToken, tenant.id, locationForm);
      toast.success("Lokasyon başarıyla eklendi!");
      setShowLocationModal(false);
      resetLocationForm();
      fetchLocations();
    } catch (err) {
      console.error("Lokasyon eklenemedi:", err);
      toast.error(err?.response?.data?.detail || "Lokasyon eklenemedi!");
    } finally {
      setSubmittingLocation(false);
    }
  };

  // Lokasyon düzenleme aç
  const handleOpenEditLocation = (location) => {
    setEditingLocation(location);
    setEditLocationForm({
      name: location.name || "",
      address: location.address || "",
    });
    setShowEditLocationModal(true);
  };

  // Lokasyon güncelleme
  const handleUpdateLocation = async () => {
    if (!editLocationForm.name.trim()) {
      toast.error("Lokasyon adı boş olamaz!");
      return;
    }

    setSubmittingLocation(true);
    try {
      await updateTenantLocation(accessToken, tenant.id, editingLocation.id, editLocationForm);
      toast.success("Lokasyon başarıyla güncellendi!");
      setShowEditLocationModal(false);
      setEditingLocation(null);
      fetchLocations();
    } catch (err) {
      console.error("Lokasyon güncellenemedi:", err);
      toast.error(err?.response?.data?.detail || "Lokasyon güncellenemedi!");
    } finally {
      setSubmittingLocation(false);
    }
  };

  // Lokasyon silme dialog aç
  const handleOpenDeleteLocation = (location) => {
    setDeletingLocation(location);
    setShowDeleteLocationDialog(true);
  };

  // Lokasyon silme
  const handleConfirmDeleteLocation = async () => {
    if (!deletingLocation) return;

    setDeletingLocationLoading(true);
    try {
      await deleteTenantLocation(accessToken, tenant.id, deletingLocation.id);
      toast.success("Lokasyon başarıyla silindi!");
      setShowDeleteLocationDialog(false);
      setDeletingLocation(null);
      fetchLocations();
    } catch (err) {
      console.error("Lokasyon silinemedi:", err);
      toast.error(err?.response?.data?.detail || "Lokasyon silinemedi!");
    } finally {
      setDeletingLocationLoading(false);
    }
  };

  // Belirli bir lokasyona ait edge'leri getir
  const fetchLocationEdges = async (locationId) => {
    if (!accessToken) return;

    setLoadingLocationEdges(prev => ({ ...prev, [locationId]: true }));

    try {
      const data = await getTenantEdges(accessToken, tenant.id, {
        location_id: locationId,
        limit: 100,
      });

      setLocationEdges(prev => ({
        ...prev,
        [locationId]: data.items || []
      }));
    } catch (err) {
      console.error(`Lokasyon ${locationId} edge'leri alınamadı:`, err);
    } finally {
      setLoadingLocationEdges(prev => ({ ...prev, [locationId]: false }));
    }
  };

  // Lokasyon satırı toggle
  const handleLocationRowToggle = (locationId) => {
    const newOpenIds = new Set(openLocationIds);

    if (newOpenIds.has(locationId)) {
      newOpenIds.delete(locationId);
    } else {
      newOpenIds.add(locationId);
      // İlk kez açılıyorsa edge'leri fetch et
      if (!locationEdges[locationId]) {
        fetchLocationEdges(locationId);
      }
    }

    setOpenLocationIds(newOpenIds);
  };

  // Edge'i lokasyondan çıkarma dialog aç
  const handleOpenRemoveEdge = (edge) => {
    setRemovingEdge(edge);
    setShowRemoveEdgeDialog(true);
  };

  // Edge'i lokasyondan çıkar
  const handleConfirmRemoveEdge = async () => {
    if (!removingEdge) return;

    setRemovingEdgeLoading(true);
    try {
      await removeEdgeFromLocation(accessToken, removingEdge.id);
      toast.success(`"${removingEdge.edgeId}" lokasyondan çıkarıldı!`);
      setShowRemoveEdgeDialog(false);
      setRemovingEdge(null);

      // Lokasyon edge'lerini yenile
      const locationId = removingEdge.location_id;
      if (locationId) {
        fetchLocationEdges(locationId);
      }

      // Ana edge listesini de yenile
      if (edgesFetched) {
        fetchEdges();
      }
    } catch (err) {
      console.error("Edge lokasyondan çıkarılamadı:", err);
      toast.error(err?.response?.data?.detail || "Edge lokasyondan çıkarılamadı!");
    } finally {
      setRemovingEdgeLoading(false);
    }
  };

  return (
    <>
      {/* Ana Tenant Satırı */}
      <TableRow sx={{ backgroundColor: "#161b22", "&:hover": { backgroundColor: "#21262d" } }}>
        <TableCell sx={{ width: 50 }}>
          <IconButton size="small" onClick={() => setTenantOpen(!tenantOpen)} sx={{ color: "#8b949e" }}>
            {tenantOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ color: "#e6edf3", fontWeight: 600 }}>{tenant.name}</TableCell>
        <TableCell sx={{ color: "#e6edf3" }}>{tenant.contact_person || "-"}</TableCell>
        <TableCell sx={{ color: "#e6edf3" }}>
          {new Date(tenant.created_at).toLocaleDateString("tr-TR")}
        </TableCell>
        <TableCell align="center">
          <IconButton size="small" sx={{ color: "#58a6ff" }} title="Düzenle" onClick={() => setShowEditTenantModal(true)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" sx={{ color: "#f85149", ml: 1 }} title="Sil" onClick={() => setShowDeleteTenantDialog(true)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>

      {/* Tenant Açıldığında Görünen İç Bölüm */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
          <Collapse in={tenantOpen} timeout="auto" unmountOnExit>
            <Box sx={{ backgroundColor: "#0d1117", borderRadius: 1, my: 1, mx: 2 }}>

              {/* Kullanıcılar Alt Satırı */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  py: 1.5,
                  px: 2,
                  borderBottom: "1px solid #21262d",
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#161b22" }
                }}
                onClick={handleUsersToggle}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <IconButton size="small" sx={{ color: "#58a6ff", p: 0.5 }}>
                    {usersOpen ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                  </IconButton>
                  <PersonAddIcon sx={{ fontSize: 18, color: "#58a6ff" }} />
                  <Typography variant="body2" sx={{ color: "#e6edf3", fontWeight: 600 }}>
                    Kullanıcılar
                  </Typography>
                  <Box component="span" sx={{ px: 1.5, py: 0.25, backgroundColor: "#30363d", borderRadius: "12px", fontSize: 11, fontWeight: 500, color: "#8b949e" }}>
                    {usersFetched ? users.length : "..."}
                  </Box>
                </Box>
                {canManageUsers && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={(e) => { e.stopPropagation(); setShowUserModal(true); }}
                    sx={{ color: "#58a6ff", borderColor: "#30363d", fontSize: 11, "&:hover": { borderColor: "#58a6ff", backgroundColor: "rgba(88,166,255,0.1)" } }}
                  >
                    Ekle
                  </Button>
                )}
              </Box>

              {/* Kullanıcılar Listesi */}
              <Collapse in={usersOpen} timeout="auto" unmountOnExit>
                <Box sx={{ py: 2, px: 3 }}>
                  {loadingUsers ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : users.length === 0 ? (
                    <Typography variant="body2" sx={{ color: "#8b949e", textAlign: "center", py: 2 }}>
                      Bu tenant'a ait kullanıcı bulunmuyor.
                    </Typography>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: "#8b949e", fontSize: 12 }}>Ad Soyad</TableCell>
                          <TableCell sx={{ color: "#8b949e", fontSize: 12 }}>Email</TableCell>
                          <TableCell sx={{ color: "#8b949e", fontSize: 12 }}>Rol</TableCell>
                          <TableCell sx={{ color: "#8b949e", fontSize: 12 }} align="center">İşlemler</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {users.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell sx={{ color: "#e6edf3", fontSize: 13 }}>{user.full_name || "-"}</TableCell>
                            <TableCell sx={{ color: "#e6edf3", fontSize: 13 }}>{user.email}</TableCell>
                            <TableCell sx={{ color: "#e6edf3", fontSize: 13 }}>{user.tenant_role || user.global_role || "-"}</TableCell>
                            <TableCell align="center">
                              <IconButton size="small" sx={{ color: "#58a6ff" }} title="Düzenle" onClick={() => handleOpenEditUser(user)}>
                                <EditIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                              <IconButton size="small" sx={{ color: "#f85149", ml: 0.5 }} title="Sil" onClick={() => handleOpenDeleteUser(user)}>
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Box>
              </Collapse>

              {/* Edge Cihazlar Alt Satırı */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  py: 1.5,
                  px: 2,
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#161b22" }
                }}
                onClick={handleEdgesToggle}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <IconButton size="small" sx={{ color: "#238636", p: 0.5 }}>
                    {edgesOpen ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                  </IconButton>
                  <RouterIcon sx={{ fontSize: 18, color: "#238636" }} />
                  <Typography variant="body2" sx={{ color: "#e6edf3", fontWeight: 600 }}>
                    Edge Cihazlar
                  </Typography>
                  <Box component="span" sx={{ px: 1.5, py: 0.25, backgroundColor: "#30363d", borderRadius: "12px", fontSize: 11, fontWeight: 500, color: "#8b949e" }}>
                    {edgesFetched ? edgesTotal : "..."}
                  </Box>
                </Box>
                {/* {me?.global_role === "superadmin" && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={(e) => { e.stopPropagation(); if (!locationsFetched) fetchLocations(); setShowEdgeModal(true); }}
                    sx={{ color: "#238636", borderColor: "#30363d", fontSize: 11, "&:hover": { borderColor: "#238636", backgroundColor: "rgba(35,134,54,0.1)" } }}
                  >
                    Ekle
                  </Button>
                )} */}
              </Box>

              {/* Edge Cihazlar Listesi */}
              <Collapse in={edgesOpen} timeout="auto" unmountOnExit>
                <Box sx={{ py: 2, px: 3 }}>
                  {loadingEdges ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : edges.length === 0 ? (
                    <Typography variant="body2" sx={{ color: "#8b949e", textAlign: "center", py: 2 }}>
                      Bu tenant'a ait edge cihaz bulunmuyor.
                    </Typography>
                  ) : (
                    <>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ color: "#8b949e", fontSize: 12 }}>Edge ID</TableCell>
                            <TableCell sx={{ color: "#8b949e", fontSize: 12 }}>Lokasyon</TableCell>
                            <TableCell sx={{ color: "#8b949e", fontSize: 12 }}>Cihaz Adı</TableCell>
                            <TableCell sx={{ color: "#8b949e", fontSize: 12 }}>Hat Adı</TableCell>
                            <TableCell sx={{ color: "#8b949e", fontSize: 12 }} align="center">İşlemler</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {edges.map((edge) => (
                            <TableRow key={edge.id}>
                              <TableCell sx={{ color: "#e6edf3", fontSize: 13, fontWeight: 500 }}>{edge.edgeId}</TableCell>
                              <TableCell sx={{ color: "#e6edf3", fontSize: 13 }}>{edge.locationName || "-"}</TableCell>
                              <TableCell sx={{ color: "#e6edf3", fontSize: 13 }}>{edge.asset?.assetId || "-"}</TableCell>
                              <TableCell sx={{ color: "#e6edf3", fontSize: 13 }}>{edge.asset?.location || "-"}</TableCell>
                              <TableCell align="center">
                                <IconButton size="small" sx={{ color: "#238636", mr: 0.5 }} title="Dashboard'a Git" onClick={() => navigate("/home?edgeId=" + edge.id)}>
                                  <DashboardIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                                <IconButton size="small" sx={{ color: "#58a6ff" }} title="Düzenle" onClick={() => handleOpenEditEdge(edge)}>
                                  <EditIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                                <IconButton size="small" sx={{ color: "#f85149", ml: 0.5 }} title="Sil" onClick={() => handleOpenDeleteEdge(edge)}>
                                  <DeleteIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {/* Server-side pagination: Daha fazla yükle */}
                      {edges.length < edgesTotal && (
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => fetchEdges(true)}
                            disabled={loadingMoreEdges}
                            sx={{ color: "#8b949e", borderColor: "#30363d", fontSize: 12, "&:hover": { borderColor: "#8b949e" } }}
                          >
                            {loadingMoreEdges ? "Yükleniyor..." : `Daha Fazla Yükle (${edges.length}/${edgesTotal})`}
                          </Button>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </Collapse>

              {/* Lokasyonlar Alt Satırı */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  py: 1.5,
                  px: 2,
                  borderBottom: "1px solid #21262d",
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#161b22" }
                }}
                onClick={handleLocationsToggle}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <IconButton size="small" sx={{ color: "#a371f7", p: 0.5 }}>
                    {locationsOpen ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                  </IconButton>
                  <Typography variant="body2" sx={{ color: "#e6edf3", fontWeight: 600 }}>
                    📍 Lokasyonlar
                  </Typography>
                  <Box component="span" sx={{ px: 1.5, py: 0.25, backgroundColor: "#30363d", borderRadius: "12px", fontSize: 11, fontWeight: 500, color: "#8b949e" }}>
                    {locationsFetched ? locations.length : "..."}
                  </Box>
                </Box>
                {canManageUsers && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={(e) => { e.stopPropagation(); setShowLocationModal(true); }}
                    sx={{ color: "#a371f7", borderColor: "#30363d", fontSize: 11, "&:hover": { borderColor: "#a371f7", backgroundColor: "rgba(163,113,247,0.1)" } }}
                  >
                    Ekle
                  </Button>
                )}
              </Box>

              {/* Lokasyonlar Listesi */}
              <Collapse in={locationsOpen} timeout="auto" unmountOnExit>
                <Box sx={{ py: 2, px: 3 }}>
                  {loadingLocations ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : locations.length === 0 ? (
                    <Typography variant="body2" sx={{ color: "#8b949e", textAlign: "center", py: 2 }}>
                      Bu tenant'a ait lokasyon bulunmuyor.
                    </Typography>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: 40 }} />
                          <TableCell sx={{ color: "#8b949e", fontSize: 12 }}>Lokasyon Adı</TableCell>
                          <TableCell sx={{ color: "#8b949e", fontSize: 12 }}>Adres</TableCell>
                          <TableCell sx={{ color: "#8b949e", fontSize: 12 }}>Cihaz Sayısı</TableCell>
                          <TableCell sx={{ color: "#8b949e", fontSize: 12 }} align="center">İşlemler</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {locations.map((location) => (
                          <React.Fragment key={location.id}>
                            {/* Ana Lokasyon Satırı */}
                            <TableRow sx={{ "&:hover": { backgroundColor: "#161b22" } }}>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  onClick={() => handleLocationRowToggle(location.id)}
                                  sx={{ color: "#8b949e" }}
                                >
                                  {openLocationIds.has(location.id) ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                                </IconButton>
                              </TableCell>
                              <TableCell sx={{ color: "#e6edf3", fontSize: 13, fontWeight: 500 }}>
                                📍 {location.name}
                              </TableCell>
                              <TableCell sx={{ color: "#e6edf3", fontSize: 13 }}>
                                {location.address || "-"}
                              </TableCell>
                              <TableCell sx={{ color: "#8b949e", fontSize: 12 }}>
                                {locationEdges[location.id] ? (
                                  <Box component="span" sx={{ px: 1, py: 0.25, backgroundColor: "#30363d", borderRadius: "8px", fontSize: 11 }}>
                                    {locationEdges[location.id].length} cihaz
                                  </Box>
                                ) : "..."}
                              </TableCell>
                              <TableCell align="center">
                                <IconButton size="small" sx={{ color: "#58a6ff" }} title="Düzenle" onClick={() => handleOpenEditLocation(location)}>
                                  <EditIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                                <IconButton size="small" sx={{ color: "#f85149", ml: 0.5 }} title="Sil" onClick={() => handleOpenDeleteLocation(location)}>
                                  <DeleteIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </TableCell>
                            </TableRow>

                            {/* Lokasyon Açıldığında - Edge Listesi */}
                            <TableRow>
                              <TableCell colSpan={5} sx={{ py: 0, borderBottom: openLocationIds.has(location.id) ? "1px solid #21262d" : "none" }}>
                                <Collapse in={openLocationIds.has(location.id)} timeout="auto" unmountOnExit>
                                  <Box sx={{ py: 2, px: 4, backgroundColor: "#0d1117" }}>
                                    {loadingLocationEdges[location.id] ? (
                                      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                                        <CircularProgress size={20} />
                                      </Box>
                                    ) : !locationEdges[location.id] || locationEdges[location.id].length === 0 ? (
                                      <Typography variant="body2" sx={{ color: "#8b949e", textAlign: "center", py: 1, fontSize: 12 }}>
                                        Bu lokasyonda henüz cihaz bulunmuyor.
                                      </Typography>
                                    ) : (
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow>
                                            <TableCell sx={{ color: "#8b949e", fontSize: 11, pl: 2 }}>Edge ID</TableCell>
                                            <TableCell sx={{ color: "#8b949e", fontSize: 11 }}>Cihaz Adı</TableCell>
                                            <TableCell sx={{ color: "#8b949e", fontSize: 11 }}>Hat Adı</TableCell>
                                            <TableCell sx={{ color: "#8b949e", fontSize: 11 }} align="center">İşlemler</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {locationEdges[location.id].map((edge) => (
                                            <TableRow key={edge.id} sx={{ "&:hover": { backgroundColor: "#161b22" } }}>
                                              <TableCell sx={{ color: "#e6edf3", fontSize: 12, pl: 2 }}>
                                                🔌 {edge.edgeId}
                                              </TableCell>
                                              <TableCell sx={{ color: "#e6edf3", fontSize: 12 }}>
                                                {edge.asset?.assetId || "-"}
                                              </TableCell>
                                              <TableCell sx={{ color: "#e6edf3", fontSize: 12 }}>
                                                {edge.asset?.location || "-"}
                                              </TableCell>
                                              <TableCell align="center">
                                                <IconButton
                                                  size="small"
                                                  sx={{ color: "#f85149" }}
                                                  title="Lokasyondan Çıkar"
                                                  onClick={() => handleOpenRemoveEdge(edge)}
                                                >
                                                  <CloseIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    )}
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Box>
              </Collapse>
            </Box>


          </Collapse>
        </TableCell>
      </TableRow>

      {/* Kullanıcı Ekleme Modal */}
      <Dialog open={showUserModal} onClose={() => { setShowUserModal(false); resetUserForm(); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          Yeni Kullanıcı Ekle - {tenant.name}
          <IconButton size="small" onClick={() => { setShowUserModal(false); resetUserForm(); }} sx={{ color: "#666" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label="Ad Soyad *" fullWidth variant="outlined" value={userForm.full_name} onChange={(e) => setUserForm({ ...userForm, full_name: e.target.value })} sx={{ mb: 2 }} />
          <TextField margin="dense" label="Email *" type="email" fullWidth variant="outlined" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} sx={{ mb: 2 }} />
          <TextField margin="dense" label="Şifre *" type="password" fullWidth variant="outlined" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} sx={{ mb: 2 }} />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Rol</InputLabel>
            <Select value={userForm.tenant_role} label="Rol" onChange={(e) => setUserForm({ ...userForm, tenant_role: e.target.value })}>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="operatör">Operatör</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleCreateUser} disabled={submittingUser} sx={{ backgroundColor: "#238636", "&:hover": { backgroundColor: "#2ea043" } }}>
            {submittingUser ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Kullanıcı Düzenleme Modal */}
      <Dialog open={showEditUserModal} onClose={() => { setShowEditUserModal(false); setEditingUser(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          Kullanıcı Düzenle
          <IconButton size="small" onClick={() => { setShowEditUserModal(false); setEditingUser(null); }} sx={{ color: "#666" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label="Ad Soyad *" fullWidth variant="outlined" value={editUserForm.full_name} onChange={(e) => setEditUserForm({ ...editUserForm, full_name: e.target.value })} sx={{ mb: 2 }} />
          <TextField margin="dense" label="Email *" type="email" fullWidth variant="outlined" value={editUserForm.email} onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })} sx={{ mb: 2 }} />
          <TextField margin="dense" label="Yeni Şifre (Boş bırakılabilir)" type="password" fullWidth variant="outlined" value={editUserForm.password} onChange={(e) => setEditUserForm({ ...editUserForm, password: e.target.value })} sx={{ mb: 2 }} placeholder="Değiştirmek istemiyorsanız boş bırakın" />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Rol</InputLabel>
            <Select value={editUserForm.tenant_role} label="Rol" onChange={(e) => setEditUserForm({ ...editUserForm, tenant_role: e.target.value })}>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="operatör">Operatör</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleUpdateUser} disabled={submittingUser} sx={{ backgroundColor: "#238636", "&:hover": { backgroundColor: "#2ea043" } }}>
            {submittingUser ? "Güncelleniyor..." : "Güncelle"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Kullanıcı Silme Dialog */}
      <Dialog open={showDeleteUserDialog} onClose={() => !deletingUserLoading && setShowDeleteUserDialog(false)} maxWidth="xs" fullWidth
        slotProps={{ paper: { sx: { backgroundColor: "#161b22", border: "1px solid #f85149", borderRadius: "8px" } } }}
      >
        <DialogTitle sx={{ color: "#f85149", fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DeleteIcon />
            Kullanıcı Silme Onayı
          </Box>
          <IconButton size="small" onClick={() => setShowDeleteUserDialog(false)} disabled={deletingUserLoading} sx={{ color: "#8b949e" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#e6edf3" }}>
            <strong>"{deletingUser?.full_name}"</strong> kullanıcısını silmek istediğinize emin misiniz?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleConfirmDeleteUser} disabled={deletingUserLoading} sx={{ backgroundColor: "#f85149", "&:hover": { backgroundColor: "#da3633" } }}>
            {deletingUserLoading ? "Siliniyor..." : "Evet, Sil"}
          </Button>
        </DialogActions>
      </Dialog>


      {/* Edge Ekleme Modal */}
      {/* <Dialog open={showEdgeModal} onClose={() => { setShowEdgeModal(false); resetEdgeForm(); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Edge Cihaz Ekle - {tenant.name}
          <IconButton size="small" onClick={() => { setShowEdgeModal(false); resetEdgeForm(); }} sx={{ color: "#666" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" sx={{ color: "#8b949e", mb: 1, mt: 1 }}>
            Cihaz Bilgileri
          </Typography>
          <TextField autoFocus margin="dense" label="Edge ID *" placeholder="EDGE-TR-001" fullWidth variant="outlined" value={edgeForm.edgeId} onChange={(e) => setEdgeForm({ ...edgeForm, edgeId: e.target.value })} sx={{ mb: 2 }} />
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField margin="dense" label="Model" fullWidth variant="outlined" value={edgeForm.device.model} onChange={(e) => setEdgeForm({ ...edgeForm, device: { ...edgeForm.device, model: e.target.value } })} />
            <TextField margin="dense" label="Firmware" fullWidth variant="outlined" value={edgeForm.device.firmware} onChange={(e) => setEdgeForm({ ...edgeForm, device: { ...edgeForm.device, firmware: e.target.value } })} />
          </Box>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ color: "#8b949e", mb: 1 }}>
            Asset Bilgileri
          </Typography>
          <TextField margin="dense" label="Cihaz Adı *" fullWidth variant="outlined" value={edgeForm.asset.assetId} onChange={(e) => setEdgeForm({ ...edgeForm, asset: { ...edgeForm.asset, assetId: e.target.value } })} sx={{ mb: 2 }} />
          <TextField margin="dense" label="Tip" fullWidth variant="outlined" value={edgeForm.asset.type} onChange={(e) => setEdgeForm({ ...edgeForm, asset: { ...edgeForm.asset, type: e.target.value } })} sx={{ mb: 2 }} />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Lokasyon *</InputLabel>
            <Select
              value={edgeForm.location_id}
              label="Lokasyon *"
              onChange={(e) => setEdgeForm({ ...edgeForm, location_id: e.target.value })}
            >
              {locations.map((loc) => (
                <MenuItem key={loc.id} value={loc.id}>
                  {loc.name} {loc.address ? `- ${loc.address}` : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField margin="dense" label="Hat Adı (Fabrika İçi Konum)" fullWidth variant="outlined" value={edgeForm.asset.location} onChange={(e) => setEdgeForm({ ...edgeForm, asset: { ...edgeForm.asset, location: e.target.value } })} sx={{ mb: 2 }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleCreateEdge} disabled={submittingEdge} sx={{ backgroundColor: "#238636", "&:hover": { backgroundColor: "#2ea043" } }}>
            {submittingEdge ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </DialogActions>
      </Dialog> */}

      {/* Edge Düzenleme Modal */}
      <Dialog open={showEditEdgeModal} onClose={() => { setShowEditEdgeModal(false); setEditingEdge(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Edge Cihaz Düzenle
          <IconButton size="small" onClick={() => { setShowEditEdgeModal(false); setEditingEdge(null); }} sx={{ color: "#666" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" sx={{ color: "#8b949e", mb: 1, mt: 1 }}>
            Cihaz Bilgileri
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Edge ID *"
            fullWidth
            variant="outlined"
            value={editEdgeForm.edgeId}
            onChange={(e) => setEditEdgeForm({ ...editEdgeForm, edgeId: e.target.value })}
            disabled={me?.global_role !== "superadmin"}
          />
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              margin="dense"
              label="Model"
              fullWidth
              variant="outlined"
              value={editEdgeForm.device.model}
              onChange={(e) => setEditEdgeForm({ ...editEdgeForm, device: { ...editEdgeForm.device, model: e.target.value } })}
              disabled={me?.global_role !== "superadmin"} 
            />
            <TextField
              margin="dense"
              label="Firmware"
              fullWidth
              variant="outlined"
              value={editEdgeForm.device.firmware}
              onChange={(e) => setEditEdgeForm({ ...editEdgeForm, device: { ...editEdgeForm.device, firmware: e.target.value } })}
              disabled={me?.global_role !== "superadmin"} 
            />
          </Box>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ color: "#8b949e", mb: 1 }}>
            Asset Bilgileri
          </Typography>
          <TextField margin="dense" label="Cihaz Adı *" fullWidth variant="outlined" value={editEdgeForm.asset.assetId} onChange={(e) => setEditEdgeForm({ ...editEdgeForm, asset: { ...editEdgeForm.asset, assetId: e.target.value } })} sx={{ mb: 2 }} />
          <TextField margin="dense" label="Tip" fullWidth variant="outlined" value={editEdgeForm.asset.type} onChange={(e) => setEditEdgeForm({ ...editEdgeForm, asset: { ...editEdgeForm.asset, type: e.target.value } })} sx={{ mb: 2 }} />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Lokasyon *</InputLabel>
            <Select
              value={editEdgeForm.location_id}
              label="Lokasyon *"
              onChange={(e) => setEditEdgeForm({ ...editEdgeForm, location_id: e.target.value })}
            >
              {locations.map((loc) => (
                <MenuItem key={loc.id} value={loc.id}>
                  {loc.name} {loc.address ? `- ${loc.address}` : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField margin="dense" label="Hat Adı (Fabrika İçi Konum)" fullWidth variant="outlined" value={editEdgeForm.asset.location} onChange={(e) => setEditEdgeForm({ ...editEdgeForm, asset: { ...editEdgeForm.asset, location: e.target.value } })} sx={{ mb: 2 }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleUpdateEdge} disabled={submittingEdge} sx={{ backgroundColor: "#238636", "&:hover": { backgroundColor: "#2ea043" } }}>
            {submittingEdge ? "Güncelleniyor..." : "Güncelle"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edge Silme Dialog */}
      <Dialog open={showDeleteEdgeDialog} onClose={() => !deletingEdgeLoading && setShowDeleteEdgeDialog(false)} maxWidth="xs" fullWidth
        slotProps={{ paper: { sx: { backgroundColor: "#161b22", border: "1px solid #f85149", borderRadius: "8px" } } }}
      >
        <DialogTitle sx={{ color: "#f85149", fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DeleteIcon />
            Edge Cihaz Silme Onayı
          </Box>
          <IconButton size="small" onClick={() => setShowDeleteEdgeDialog(false)} disabled={deletingEdgeLoading} sx={{ color: "#8b949e" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#e6edf3" }}>
            <strong>"{deletingEdge?.edgeId}"</strong> edge cihazını silmek istediğinize emin misiniz?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleConfirmDeleteEdge} disabled={deletingEdgeLoading} sx={{ backgroundColor: "#f85149", "&:hover": { backgroundColor: "#da3633" } }}>
            {deletingEdgeLoading ? "Siliniyor..." : "Evet, Sil"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lokasyon Ekleme Modal */}
      <Dialog open={showLocationModal} onClose={() => { setShowLocationModal(false); resetLocationForm(); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          Yeni Lokasyon Ekle - {tenant.name}
          <IconButton size="small" onClick={() => { setShowLocationModal(false); resetLocationForm(); }} sx={{ color: "#666" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Lokasyon Adı *"
            fullWidth
            variant="outlined"
            value={locationForm.name}
            onChange={(e) => setLocationForm({ ...locationForm, name: e.target.value })}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            margin="dense"
            label="Adres"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={locationForm.address}
            onChange={(e) => setLocationForm({ ...locationForm, address: e.target.value })}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleCreateLocation} disabled={submittingLocation} sx={{ backgroundColor: "#238636", "&:hover": { backgroundColor: "#2ea043" } }}>
            {submittingLocation ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lokasyon Düzenleme Modal */}
      <Dialog open={showEditLocationModal} onClose={() => { setShowEditLocationModal(false); setEditingLocation(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          Lokasyon Düzenle
          <IconButton size="small" onClick={() => { setShowEditLocationModal(false); setEditingLocation(null); }} sx={{ color: "#666" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Lokasyon Adı *"
            fullWidth
            variant="outlined"
            value={editLocationForm.name}
            onChange={(e) => setEditLocationForm({ ...editLocationForm, name: e.target.value })}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            margin="dense"
            label="Adres"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={editLocationForm.address}
            onChange={(e) => setEditLocationForm({ ...editLocationForm, address: e.target.value })}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleUpdateLocation} disabled={submittingLocation} sx={{ backgroundColor: "#238636", "&:hover": { backgroundColor: "#2ea043" } }}>
            {submittingLocation ? "Güncelleniyor..." : "Güncelle"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lokasyon Silme Dialog */}
      <Dialog open={showDeleteLocationDialog} onClose={() => !deletingLocationLoading && setShowDeleteLocationDialog(false)} maxWidth="xs" fullWidth
        slotProps={{ paper: { sx: { backgroundColor: "#161b22", border: "1px solid #f85149", borderRadius: "8px" } } }}
      >
        <DialogTitle sx={{ color: "#f85149", fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DeleteIcon />
            Lokasyon Silme Onayı
          </Box>
          <IconButton size="small" onClick={() => setShowDeleteLocationDialog(false)} disabled={deletingLocationLoading} sx={{ color: "#8b949e" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#e6edf3" }}>
            <strong>"{deletingLocation?.name}"</strong> lokasyonunu silmek istediğinize emin misiniz?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleConfirmDeleteLocation} disabled={deletingLocationLoading} sx={{ backgroundColor: "#f85149", "&:hover": { backgroundColor: "#da3633" } }}>
            {deletingLocationLoading ? "Siliniyor..." : "Evet, Sil"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tenant Düzenleme Modal */}
      <Dialog open={showEditTenantModal} onClose={() => setShowEditTenantModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Tenant Düzenle
          <IconButton size="small" onClick={() => setShowEditTenantModal(false)} sx={{ color: "#666" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Tenant Adı *"
            fullWidth
            variant="outlined"
            value={editTenantForm.name}
            onChange={(e) => setEditTenantForm({ ...editTenantForm, name: e.target.value })}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            margin="dense"
            label="Yetkili Kişi"
            fullWidth
            variant="outlined"
            value={editTenantForm.contact_person}
            onChange={(e) => setEditTenantForm({ ...editTenantForm, contact_person: e.target.value })}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleUpdateTenant} disabled={submittingTenant} sx={{ backgroundColor: "#238636", "&:hover": { backgroundColor: "#2ea043" } }}>
            {submittingTenant ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tenant Silme Dialog */}
      <Dialog open={showDeleteTenantDialog} onClose={() => !deletingTenant && setShowDeleteTenantDialog(false)} maxWidth="xs" fullWidth
        slotProps={{ paper: { sx: { backgroundColor: "#161b22", border: "1px solid #f85149", borderRadius: "8px" } } }}
      >
        <DialogTitle sx={{ color: "#f85149", fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DeleteIcon />
            Tenant Silme Onayı
          </Box>
          <IconButton size="small" onClick={() => setShowDeleteTenantDialog(false)} disabled={deletingTenant} sx={{ color: "#8b949e" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#e6edf3", mb: 2 }}>
            <strong>"{tenant.name}"</strong> tenant'ını silmek istediğinize emin misiniz?
          </Typography>
          <Typography sx={{ color: "#f85149", fontSize: 14, backgroundColor: "rgba(248,81,73,0.1)", p: 1.5, borderRadius: 1 }}>
            Bu işlem tenant'a bağlı tüm kullanıcıları ve edge cihazlarını da silecektir.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleDeleteTenant} disabled={deletingTenant} sx={{ backgroundColor: "#f85149", "&:hover": { backgroundColor: "#da3633" } }}>
            {deletingTenant ? "Siliniyor..." : "Evet, Sil"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edge Lokasyondan Çıkarma Dialog */}
      <Dialog
        open={showRemoveEdgeDialog}
        onClose={() => !removingEdgeLoading && setShowRemoveEdgeDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#0d1117",
            border: "1px solid #30363d",
            borderRadius: "12px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.4)"
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: "1px solid #21262d",
          pb: 2,
          display: "flex",
          alignItems: "center",
          gap: 2
        }}>
          <Box sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            backgroundColor: "rgba(255,152,0,0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}>
            <CloseIcon sx={{ fontSize: 24, color: "#ff9800" }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ color: "#e6edf3", fontWeight: 600, mb: 0.5 }}>
              Lokasyondan Çıkar
            </Typography>
            <Typography variant="body2" sx={{ color: "#8b949e", fontSize: 13 }}>
              Cihaz atama işlemi kaldırılacak
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => setShowRemoveEdgeDialog(false)}
            disabled={removingEdgeLoading}
            sx={{ color: "#8b949e", "&:hover": { color: "#e6edf3" } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Box sx={{
            backgroundColor: "#161b22",
            border: "1px solid #30363d",
            borderRadius: "8px",
            p: 2.5,
            mb: 2.5
          }}>
            <Typography sx={{ color: "#e6edf3", fontSize: 15, mb: 1 }}>
              <strong style={{ color: "#58a6ff" }}>"{removingEdge?.edgeId}"</strong> cihazını bu lokasyondan çıkarmak istediğinize emin misiniz?
            </Typography>
            <Typography sx={{ color: "#8b949e", fontSize: 13, lineHeight: 1.6 }}>
              Cihaz başka bir lokasyona atanabilir veya lokasyonsuz kalabilir.
            </Typography>
          </Box>

          <Box sx={{
            display: "flex",
            gap: 1.5,
            p: 2,
            backgroundColor: "rgba(56,139,253,0.05)",
            border: "1px solid rgba(56,139,253,0.2)",
            borderRadius: "8px"
          }}>
            <Box sx={{ color: "#58a6ff", mt: 0.25 }}>
              <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 1.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13zM0 8a8 8 0 1116 0A8 8 0 010 8zm6.5-.25A.75.75 0 017.25 7h1a.75.75 0 01.75.75v2.75h.25a.75.75 0 010 1.5h-2a.75.75 0 010-1.5h.25v-2h-.25a.75.75 0 01-.75-.75zM8 6a1 1 0 100-2 1 1 0 000 2z"></path>
              </svg>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: "#58a6ff", fontSize: 13, fontWeight: 600, mb: 0.5 }}>
                Cihaz silinmeyecek
              </Typography>
              <Typography sx={{ color: "#8b949e", fontSize: 12, lineHeight: 1.5 }}>
                Bu işlem sadece lokasyon atamasını kaldırır. Cihazı tamamen silmek için "Edge Cihazlar" bölümünü kullanın.
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{
          borderTop: "1px solid #21262d",
          px: 3,
          py: 2,
          gap: 1.5
        }}>
          <Button
            variant="outlined"
            onClick={() => setShowRemoveEdgeDialog(false)}
            disabled={removingEdgeLoading}
            sx={{
              color: "#8b949e",
              borderColor: "#30363d",
              "&:hover": {
                borderColor: "#8b949e",
                backgroundColor: "rgba(139,148,158,0.1)"
              }
            }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmRemoveEdge}
            disabled={removingEdgeLoading}
            startIcon={removingEdgeLoading ? <CircularProgress size={16} /> : null}
            sx={{
              backgroundColor: "#ff9800",
              color: "#fff",
              fontWeight: 600,
              "&:hover": {
                backgroundColor: "#f57c00"
              }
            }}
          >
            {removingEdgeLoading ? "Çıkarılıyor..." : "Lokasyondan Çıkar"}
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
}

export default function TenantList() {
  const accessToken = useSelector((s) => s.session?.accessToken);
  const me = useSelector((s) => s.session?.me);
  const pageTitle = me?.global_role === "superadmin" ? "Tenantlar" : "Kullanıcılar";

  // Server-side pagination state
  const [tenants, setTenants] = useState([]);
  const [tenantsTotal, setTenantsTotal] = useState(0);
  const [tenantsOffset, setTenantsOffset] = useState(0);
  const [tenantsLimit] = useState(20);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    contact_person: "",
  });

  const fetchTenants = async (loadMore = false) => {
    if (!accessToken) {
      setLoading(false);
      return;
    }

    if (loadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setTenantsOffset(0);
    }

    try {
      const currentOffset = loadMore ? tenantsOffset : 0;
      const data = await getTenants(accessToken, {
        limit: tenantsLimit,
        offset: currentOffset,
      });

      const items = data.items || [];
      const total = data.total || 0;

      if (loadMore) {
        setTenants(prev => [...prev, ...items]);
      } else {
        setTenants(items);
      }

      setTenantsTotal(total);
      setTenantsOffset(currentOffset + items.length);
    } catch (err) {
      console.error("Tenant listesi alınamadı:", err);
      toast.error("Tenant listesi alınamadı!");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchTenants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const resetForm = () => {
    setFormData({
      name: "",
      contact_person: "",
    });
  };

  const handleCreateTenant = async () => {
    if (!formData.name.trim()) {
      toast.error("Firma/Tenant adı boş olamaz!");
      return;
    }

    setSubmitting(true);
    try {
      await createTenant(accessToken, formData);
      toast.success("Tenant başarıyla oluşturuldu!");
      setShowAddModal(false);
      resetForm();
      fetchTenants();
    } catch (err) {
      console.error("Tenant oluşturulamadı:", err);
      toast.error(err?.response?.data?.detail || "Tenant oluşturulamadı!");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ color: "#e6edf3" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold", color: "#e6edf3" }}>
            {pageTitle}
          </Typography>
        </Box>
        {me?.global_role === "superadmin" && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowAddModal(true)}
            sx={{ backgroundColor: "#238636", "&:hover": { backgroundColor: "#2ea043" } }}
          >
            YENİ Tenant
          </Button>
        )}
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          backgroundColor: "#161b22",
          border: "1px solid #30363d",
          borderRadius: "8px",
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#21262d" }}>
              <TableCell sx={{ width: 50 }} />
              <TableCell sx={{ color: "#8b949e", fontWeight: 600 }}>Firma Adı</TableCell>
              <TableCell sx={{ color: "#8b949e", fontWeight: 600 }}>Yetkili Kişi</TableCell>
              <TableCell sx={{ color: "#8b949e", fontWeight: 600 }}>Oluşturulma Tarihi</TableCell>
              <TableCell sx={{ color: "#8b949e", fontWeight: 600 }} align="center">İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tenants.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} sx={{ color: "#8b949e", textAlign: "center", py: 4 }}>
                  Henüz tenant bulunmuyor.
                </TableCell>
              </TableRow>
            ) : (
              tenants.map((tenant) => (
                <TenantRow key={tenant.id} tenant={tenant} accessToken={accessToken} me={me} onRefresh={fetchTenants} />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Server-side pagination: Daha fazla yükle */}
      {tenants.length > 0 && tenants.length < tenantsTotal && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Button
            variant="outlined"
            onClick={() => fetchTenants(true)}
            disabled={loadingMore}
            sx={{ color: "#8b949e", borderColor: "#30363d", "&:hover": { borderColor: "#8b949e" } }}
          >
            {loadingMore ? "Yükleniyor..." : `Daha Fazla Yükle (${tenants.length}/${tenantsTotal})`}
          </Button>
        </Box>
      )}

      {/* Tenant Ekleme Modal */}
      <Dialog open={showAddModal} onClose={() => { setShowAddModal(false); resetForm(); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Yeni Tenant Ekle
          <IconButton size="small" onClick={() => { setShowAddModal(false); resetForm(); }} sx={{ color: "#666" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label="Firma / Tenant Adı *" fullWidth variant="outlined" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} sx={{ mb: 2 }} />
          <TextField margin="dense" label="Yetkili Kişi" fullWidth variant="outlined" value={formData.contact_person} onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleCreateTenant} disabled={submitting} sx={{ backgroundColor: "#238636", "&:hover": { backgroundColor: "#2ea043" } }}>
            {submitting ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
